import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { API_ENDPOINT } from 'api/constants';
import { SSEEventType } from 'constants/sse';
import { RootState } from 'store';
import { setConnectionStatus, setData } from 'store/slices/sseSlice';

let eventSourceInstance: EventSource | null = null;
const isServer = typeof window === 'undefined';
const REMOVE_SURROUNDING_QUOTES_REGEX = /^"|"$/g;

const getEventSource = () => {
  if (isServer) return null;

  if (!eventSourceInstance) {
    const token = localStorage.getItem('token')?.replace(REMOVE_SURROUNDING_QUOTES_REGEX, '');
    const eroGuestId = localStorage.getItem('ero_guest_id')?.replace(REMOVE_SURROUNDING_QUOTES_REGEX, '');

    const urlObj = new URL(`${API_ENDPOINT}/sse/init`);
    if (token) urlObj.searchParams.append('token', token);
    if (eroGuestId) urlObj.searchParams.append('ero_guest_id', eroGuestId);

    eventSourceInstance = new EventSource(urlObj.toString());
  }
  return eventSourceInstance;
};

const useSSEConnection = (currentUser?: unknown) => {
  const dispatch = useDispatch();

  useEffect(() => {
    if (isServer || !currentUser) {
      return;
    }

    const eventSource = getEventSource();
    if (!eventSource) return;

    const handleConnectionEstablished = (event: MessageEvent) => {
      const data = JSON.parse(event.data);
      dispatch(setConnectionStatus(true));
      dispatch(setData({ ...data, eventType: SSEEventType.CONNECTION_ESTABLISHED }));
    };

    const handleHeartbeat = (event: MessageEvent) => {
      const data = JSON.parse(event.data);
      dispatch(setData({ ...data, eventType: SSEEventType.HEARTBEAT }));
    };

    const handleError = () => {
      dispatch(setConnectionStatus(false));

      setTimeout(() => {
        eventSourceInstance?.close();
        eventSourceInstance = null;
        getEventSource();
      }, 5000);
    };

    const handleCustomDialogPhotoGen = (event: MessageEvent) => {
      const data = JSON.parse(event.data);
      dispatch(setData({ ...data, eventType: SSEEventType.CUSTOM_DIALOG_PHOTO_GEN, taskId: data.taskId }));
    };

    const handleCustomDialogVideoGen = (event: MessageEvent) => {
      const data = JSON.parse(event.data);
      dispatch(setData({ ...data, eventType: SSEEventType.CUSTOM_DIALOG_VIDEO_GEN, taskId: data.task_id }));
    };

    const handleAvatarGenerated = (event: MessageEvent) => {
      const data = JSON.parse(event.data);
      dispatch(setData({ ...data, eventType: SSEEventType.AVATAR_GENERATED }));
    };

    const handlePermanentClose = () => {
      eventSourceInstance?.close();
      eventSourceInstance = null;
      dispatch(setConnectionStatus(false));
    };

    // Очистка перед добавлением новых слушателей (защита от дублирования)
    eventSource.removeEventListener(SSEEventType.CONNECTION_ESTABLISHED, handleConnectionEstablished);
    eventSource.removeEventListener(SSEEventType.HEARTBEAT, handleHeartbeat);
    eventSource.removeEventListener(SSEEventType.ERROR, handleError);
    eventSource.removeEventListener(SSEEventType.CUSTOM_DIALOG_PHOTO_GEN, handleCustomDialogPhotoGen);
    eventSource.removeEventListener(SSEEventType.CUSTOM_DIALOG_VIDEO_GEN, handleCustomDialogVideoGen);
    eventSource.removeEventListener(SSEEventType.AVATAR_GENERATED, handleAvatarGenerated);

    eventSource.addEventListener(SSEEventType.CONNECTION_ESTABLISHED, handleConnectionEstablished);
    eventSource.addEventListener(SSEEventType.HEARTBEAT, handleHeartbeat);
    eventSource.addEventListener(SSEEventType.ERROR, handleError);
    eventSource.addEventListener(SSEEventType.CUSTOM_DIALOG_PHOTO_GEN, handleCustomDialogPhotoGen);
    eventSource.addEventListener(SSEEventType.CUSTOM_DIALOG_VIDEO_GEN, handleCustomDialogVideoGen);
    eventSource.addEventListener(SSEEventType.AVATAR_GENERATED, handleAvatarGenerated);
    eventSource.addEventListener(SSEEventType.PERMANENT_CLOSE, handlePermanentClose);

    return () => {
      eventSource.removeEventListener(SSEEventType.CONNECTION_ESTABLISHED, handleConnectionEstablished);
      eventSource.removeEventListener(SSEEventType.HEARTBEAT, handleHeartbeat);
      eventSource.removeEventListener(SSEEventType.ERROR, handleError);
      eventSource.removeEventListener(SSEEventType.CUSTOM_DIALOG_PHOTO_GEN, handleCustomDialogPhotoGen);
      eventSource.removeEventListener(SSEEventType.CUSTOM_DIALOG_VIDEO_GEN, handleCustomDialogVideoGen);
      eventSource.removeEventListener(SSEEventType.AVATAR_GENERATED, handleAvatarGenerated);
      eventSource.removeEventListener(SSEEventType.PERMANENT_CLOSE, handlePermanentClose);
    };
  }, [dispatch, currentUser, isServer]);

  return {
    isConnected: useSelector((state: RootState) => state.sse?.isConnected ?? false),
    error: useSelector((state: RootState) => state.sse?.error ?? null)
  };
};

export default useSSEConnection;
